import React, { useState, useEffect, useRef } from 'react';
import { Input, Box } from '@updater/ui-uds';
import { useSingleLineAddressInputContext } from './context';

export interface UnitFieldProps {
  label?: string;
  placeholder?: string;
  error?: string;
  name?: string;
}

export function UnitField({
  label = 'Apt/Unit #',
  placeholder,
  error = 'An apartment or unit number is required.',
  name = 'unit',
}: UnitFieldProps) {
  const { state, data, onEnterUnit } = useSingleLineAddressInputContext();
  const [value, setValue] = useState(data.currentAddress?.unit ?? '');
  const [hasBlurred, setHasBlurred] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync with data when address changes (populates existing unit or clears on new selection)
  useEffect(() => {
    setValue(data.currentAddress?.unit ?? '');
    setHasBlurred(false);
  }, [data.currentAddress?.street, data.currentAddress?.unit]);

  // Auto-focus when unit is required
  useEffect(() => {
    if (state === 'needsUnit') {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [state]);

  const hasUnit = !!data.currentAddress?.unit;
  const isUnitRequired = state === 'needsUnit';

  if (state === 'empty' || state === 'complete') return null;
  if (!hasUnit && !isUnitRequired) return null;

  const showError = hasBlurred && !value && isUnitRequired;
  const placeholderProps = placeholder
    ? { placeholder, className: 'input-with-placeholder' }
    : {};

  return (
    <Box mt="m">
      <Input
        inputRef={inputRef}
        label={label}
        {...placeholderProps}
        name={name}
        value={value}
        error={showError ? error : undefined}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const newValue = e.target.value;
          setValue(newValue);
          onEnterUnit(newValue);
        }}
        onBlur={() => setHasBlurred(true)}
        autoComplete="address-line2"
      />
    </Box>
  );
}
